import { LMFormatterService } from './lm-formatter.service';
import { LMListCardService } from '@list/services/lm-list-card.service';
import { NavigationResource } from '@resources/navigation.resource';
import { LMUrlUtilService } from '@services/utils';
import { lmProcessIndicatorFormatter } from './lm-process-indicator.formatter';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace lmCardViewFormatter {
  export const format = (
    lmFormatterService: LMFormatterService,
    lmListCardService: LMListCardService,
    lmUrlUtilService: LMUrlUtilService,
    row: number,
    cell: number,
    value: any,
    columnDef: ListGridColumn,
    dataView: LMListDataView,
    dataGrid: SohoDataGridStatic,
    /**
     * containerClass - The class placed on the surrounding div of the card.
     * This is useful for different margins/ padding depending on the context it's used in.
     */
    containerClass: string
  ): string => {
    if (dataView.isTreeMoreLink) {
      return lmFormatterService.getMoreLink(cell, dataView);
    } else if (lmFormatterService.isTotalRow(dataView)) {
      return lmFormatterService.formatTotalRowColumn(row, cell, value, columnDef, dataView, dataGrid);
    } else {
      const card = getCardTemplateData(lmListCardService, columnDef.displayField, dataView);

      if (card === undefined) {
        return '';
      }

      /**
       * Note:
       * This template should match the markup and CSS used in lm-list-card.component.html
       */
      const cardViewTemplate = `
        <div class="lm-card-view">

          ${card.imageUrl ? getImageHTML(card) : ''}
          ${!card.imageUrl && card.isMissingImage && !card.imageForegroundText ? getMissingImageHTMLAsSVG(card) : ''}
          ${card.isMissingImage && card.imageForegroundText ? getAvatarHTML(card) : ''}

          <div class="lm-card-content">
            <div class="lm-card-flex">
              ${card.columns.map( (col, colIndex) => `
                  <div class="lm-column">
                    <ul>
                      ${col.column.map((item, index) => `
                          <li class="${!item.displayAsTag ? 'lm-font-md' : ''}">
                            ${index === 0 && colIndex === 0 ? `<h3 class="lm-font-lg">` : ''}
                            <div class="lm-card-row ${!item.displayAsTag && item.isExternalNavigation || !item.displayAsTag && item.isInternalNavigation ? 'lm-hyperlink-container' : ''}">
                              ${item.label ? `<label class="lm-font-md">${item.label}: </label>` : ''}
                              ${item.alertCondition ? getAlertHTML(item) : ''}
                              ${item.displayAsTag || isField(item) ? `${getFieldValue(item, dataView, lmFormatterService, lmUrlUtilService)}` : ''}
                              ${item.isProcessIndicator ? `${getProcessIndicatorHTML(item)}` : ''}
                              ${!item.displayAsTag && isExternalNavigation(item) ? getExternalNavigationHTML(item) : ''}
                              ${!item.displayAsTag && isInternalNavigation(item) ? getInternalNavigationHTML(item, dataView, lmFormatterService, lmUrlUtilService) : ''}
                            </div>
                            ${index === 0 ? `</h3>` : ''}
                          </li>`).join('')}
                    </ul>
                  </div>`).join('')}
            </div>
          </div>
        </div>

        <!-- button  row -->
        ${card.buttonRow.length > 0 ? getButtonRowHTML(card, lmUrlUtilService, lmListCardService) : ''}`;


      let html = '<div class="' + containerClass + '">' + cardViewTemplate + '</div>';

      if (dataView?.doesNotExist) {
        html = '<del>' + html + '</del>';
      }

      return html;
    }
  };

  export const getAvatarHTML = (card: LMCardTemplateData): string => `
    <div class="${card.cardImageClasses}">
      <span class="${card.bgColorClass}">${card.imageForegroundText}</span>
    </div>`;

  export const getImageHTML = (card: LMCardTemplateData): string => {
    const altText = card.imageForegroundText || '';
    return `<div class="${card.cardImageClasses}"><img src="${card.imageUrl}" alt="${altText}"/></div>`;
  };

  export const getMissingImageHTMLAsSVG = (card: LMCardTemplateData): string => `
    <div class="${card.cardImageClasses}">
      <svg class="icon avatar" focusable="false" aria-hidden="true" role="presentation">
        <use xlink:href="#icon-user-avatar"></use>
      </svg>
    </div>`;

  export const getInternalNavigationHTML = (item: LMListViewTemplateField, dataView: LMDataView, lmFormatterService: LMFormatterService, lmUrlUtilService: LMUrlUtilService): string => {
    const isValid = lmFormatterService.isNavigationNameValid(item.navigationParams.navigationName, dataView);
    if (isValid) {
      const markup = `
        <a href="${buildUrlNavigation(item, lmUrlUtilService)}"
            class="hyperlink lm-card-internal-link lm-font-md"
            data-navigation-name='${item.navigationParams.navigationName}'
            data-automation-id='${item?.fieldId}'
        >${item.displayValue}</a>`;

      return markup;

    } else {
      return `<span>${item.displayValue}</span>`;
    }
  };

  export const buildUrlNavigation = (item, lmUrlUtilService): string => {
    const navigationResource = new NavigationResource({ resourceId: item.navigationParams.dataView.resourceId, actionName: item.navigationParams.navigationName });
    return lmUrlUtilService.buildUrl({ resource: navigationResource }, true);
  };

  export const getExternalNavigationHTML = (item) => `<a href="${item.httpUrl}" class="hyperlink lm-font-md">${item.displayValue}</a>`;

  export const getAlertHTML = (item): string => {
    const markup = `
      <label class="lm-card-alert lm-font-md" title="${item.alertCondition.alertTooltip}">
        <svg class="icon datagrid-alert-icon icon-${item.alertCondition.icon}">
          <use xlink:href="#icon-${item.alertCondition.icon}"</use>
        </svg>
      </label>`;

    return markup;
  };

  export const getButtonRowHTML = (card: LMCardTemplateData, lmUrlUtilService: LMUrlUtilService, lmListCardService: LMListCardService): string => {
    const markup = `
      <div class="lm-card-button-row">
          ${card.buttonRow.map(button => `
                ${button.isDisplayAsLink && !button.isIconOnly ? getButtonAsLink(button, lmUrlUtilService) : ''}
                ${button.isIconOnly ? getButtonAsIcon(lmListCardService.encodeHtmlSingleQuote(button)) : '' }
                ${!button.isIconOnly && !button.isDisplayAsLink ? getDefaultButton(lmListCardService.encodeHtmlSingleQuote(button)) : '' }
             `).join('')}
      </div>`;

    return markup;
  };

  /** Note,
   * do not remove lm-card-button class, see onColumnClicked() in lm-datagrid-base.component
   * extra span here to match icon button used in lm-list-card.component.html
   * **/
  export const getButtonAsIcon = (button: LMCardViewButtonRowButton): string => `
    <button
      ${button.icon ? `class="btn-icon lm-icon-button lm-card-button"` : `class="btn-icon lm-icon-button lm-card-button lm-missing-soho-icon"`}
      title='${button.actionReference.tooltip || button.actionReference.label}'
      data-displayfield='${JSON.stringify(button.actionReference)}'
    >
      ${button.icon ? `<span>${button.actionReference.label}</span>` : ``}
      ${button.icon ? `
      <svg class="icon lm-icon" focusable="false" aria-hidden="true" role="presentation">
        <use xlink:href="#icon-${button.icon}"></use>
      </svg>` : `<span><span style="background-image: url('${button.iconUrl}')">${button.actionReference.label ? button.actionReference.label : `&nbsp;`}</span></span>`}
    </button>`;

  export const getButtonAsLink = (button: LMCardViewButtonRowButton, lmUrlUtilService: LMUrlUtilService): string => {
    /** Note, do not remove lm-card-button class, see onColumnClicked() in lm-datagrid-base.component **/
    let href = 'javascript:void(0);';
    let targetBlank = false;

    if (button.httpUrl && button.isOpenExternal && (button.actionReference.cardViewFormButtonSpec
      && button.actionReference.cardViewFormButtonSpec.httpUrl
      && !button.actionReference.cardViewFormButtonSpec.httpUrl.variables)) {

      href = button.httpUrl;
      targetBlank = true;
    } else if (button.navigationParams && !button.isOpenExternal) {

      href = buildUrlNavigation(button, lmUrlUtilService);
    }

    // The issue on single quote does not affect buttons displayed as link
    return `<a href="${href}" ${targetBlank ? 'target="_blank"' : ''} class="btn hyperlink lm-card-button "
      data-displayfield='${JSON.stringify(button.actionReference)}'>${button.actionReference.label}</a>`;
  };

  export const getDefaultButton = (button: LMCardViewButtonRowButton): string => {
    const classArray = [
      'btn',
      'lm-card-button'
    ];

    if (button.isPrimary) {
      classArray.push('lm-is-primary');
    } else if (button.fieldStyle && button.fieldStyle.color) {
      classArray.push('lpl-' + button.fieldStyle.color);
    }

    /** Note, do not remove lm-card-button class, see onColumnClicked() in lm-datagrid-base.component **/
    return `<button class='${classArray.join(' ')}' data-displayfield='${JSON.stringify(button.actionReference)}'>${button.actionReference.label}</button>`;
  };

  export const getCardTemplateData = (
    lmListCardService: LMListCardService,
    displayField: LMListDisplayField,
    dataView: LMListDataView
  ): LMCardTemplateData => lmListCardService.getCardTemplateData(displayField, dataView);

  export const isInternalNavigation = (item: LMListViewTemplateField): boolean => {
    if (!item || item.value === '' || !item.navigationParams) {
      return false;
    }

    return true;
  };

  export const isExternalNavigation = (item: LMListViewTemplateField): boolean => {
    if (!item || !item.httpUrl || item.httpUrl.trim().length === 0) {
      return false;
    }

    return true;
  };

  export const isField = (item: LMListViewTemplateField): boolean => {
    if (item && (item.isField || item.displayAsTag)) {
      return true;
    }

    return false;
  };

  export const getProcessIndicatorHTML = (item: LMListViewTemplateField): string => lmProcessIndicatorFormatter.getProcessIndicatorHTML(item.processIndicatorDisplayData);

  export const getFieldValue = (
    item: LMListViewTemplateField,
    dataView: LMDataView,
    lmFormatterService: LMFormatterService,
    lmUrlUtilService: LMUrlUtilService
  ): string => {
    if (item.displayAsTag) {
      const classArray = [ 'tag lm-font-md' ];

      if (item.fieldStyle && item.fieldStyle.color) {
        classArray.push('lpl-' + item.fieldStyle.color + '-bg'); // for tag background
        classArray.push('lpl-white');  // for tag foreground text when there is background color
      }

      if (isExternalNavigation(item)) {
        classArray.push('is-clickable');
        const externalLinkClasses = classArray.join(' ');
        return `
          <a href="${item.httpUrl}" class="${externalLinkClasses}">
            ${item.displayValue}
            <span>
              <svg class="icon" focusable="false" aria-hidden="true" role="presentation">
                <use xlink:href="#icon-caret-right"></use>
              </svg>
              <span class="audible"></span>
            </span>
          </a>
        `;
      }

      if (isInternalNavigation(item)) {
        const isValid = lmFormatterService.isNavigationNameValid(item.navigationParams.navigationName, dataView);
        if (isValid) {
          classArray.push('is-clickable');
          const internalLinkClasses = classArray.join(' ');
          return `
            <a href="${buildUrlNavigation(item, lmUrlUtilService)}"
                class="${internalLinkClasses}"
                data-displayfield='${JSON.stringify(item)}'
                data-navigation-params='${JSON.stringify(item.navigationParams)}'
            >
              ${item.displayValue}
              <span>
                <svg class="icon" focusable="false" aria-hidden="true" role="presentation">
                  <use xlink:href="#icon-caret-right"></use>
                </svg>
                <span class="audible"></span>
              </span>
            </a>
          `;
        }
      }

      const classes = classArray.join(' ');
      return `<span class="${classes}">${item.displayValue}</span>`;

    } else {

      if (item.fieldStyle) {
        const styles = lmFormatterService.getFieldStyleClasses(item.fieldStyle);
        return `<span class="${styles.join(' ')}">${item.displayValue}</span>`;
      } else {
        return `<span>${item.displayValue}</span>`;
      }

    }
  };
}
